import {
  createContractAnalyzerAppApiClient,
  type FolioActionExecutionResponse,
  type FolioConversationDetailResponse,
} from '@contract-analyzer/api-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  normalizeStoredPageContext,
  toApiPageContext,
} from '@/hooks/folio-page-context-contract';
import { startFolioMessageStream } from '@/hooks/folio-transport';
import {
  jsonObjectSchema,
  parseFolioMode,
} from '@/hooks/folio-payload-schemas';
import {
  parseStoredRichEvent,
} from '@/hooks/folio-event-decoder';
import {
  createMessage,
  finalizeAssistantMessage,
  removeMessagePair,
} from '@/hooks/folio-message-state';
import type {
  ActionCard,
  FolioActionExecutionResult,
  FolioMode,
  FolioStreamMessage,
  LoadedConversation,
  LastRequest,
  PageContext,
  UseFolioStreamResult,
} from '@/hooks/folio-types';
import { apiBaseUrl } from '@/lib/api';
import { withCleanup } from '@/lib/with-cleanup';

export type {
  ActionCard,
  FolioActionExecutionResult,
  FolioActionUiHandoff,
  CellConfidence,
  CellExplanation,
  ChartData,
  ChartPoint,
  ChartReferenceLine,
  CitationDocument,
  CitationGroup,
  CodeExecution,
  FieldStats,
  FolioMode,
  LoadedConversation,
  FolioStreamEvent,
  FolioStreamMessage,
  GuideStep,
  NavigationCta,
  PageContext,
  PageContextEntities,
  PageContextTransition,
  ReasoningSummary,
  ScalarData,
  TableCellValue,
  TableData,
  ToolCallTrace,
  UserQuestion,
  UserQuestionField,
} from '@/hooks/folio-types';
export { parseStoredRichEvent } from '@/hooks/folio-event-decoder';
export { applyStreamEvent } from '@/hooks/folio-message-state';

function normalizeActionExecutionResult(
  response: FolioActionExecutionResponse,
): FolioActionExecutionResult {
  const uiHandoff = response.ui_handoff;
  const result = jsonObjectSchema.safeParse(response.result);
  if (!result.success) {
    throw new Error('Sean returned an invalid action result.');
  }

  return {
    conversation_id: response.conversation_id,
    ...(response.conversation_project_id === undefined
      ? {}
      : { conversation_project_id: response.conversation_project_id }),
    status: response.status,
    action_type: response.action_type,
    user_message: response.user_message,
    assistant_message: response.assistant_message,
    ...(uiHandoff == null
      ? {}
      : {
          ui_handoff: {
            intent: uiHandoff.intent,
            ...(uiHandoff.project_id == null ? {} : { project_id: uiHandoff.project_id }),
            ...(uiHandoff.template_id == null ? {} : { template_id: uiHandoff.template_id }),
            ...(uiHandoff.run_id == null ? {} : { run_id: uiHandoff.run_id }),
            ...(uiHandoff.evaluation_run_id == null
              ? {}
              : { evaluation_run_id: uiHandoff.evaluation_run_id }),
            ...(uiHandoff.deep_link == null ? {} : { deep_link: uiHandoff.deep_link }),
            ...(uiHandoff.target_test_id == null
              ? {}
              : { target_test_id: uiHandoff.target_test_id }),
            ...(uiHandoff.page_id == null ? {} : { page_id: uiHandoff.page_id }),
            ...(uiHandoff.page_name == null ? {} : { page_name: uiHandoff.page_name }),
          },
        }),
    result: result.data,
  };
}

function mapStoredMessages(
  response: FolioConversationDetailResponse,
): readonly FolioStreamMessage[] {
  return response.messages.map((message) => ({
    id: message.message_id,
    role: message.role === 'user' ? 'user' : 'assistant',
    sourceRole: message.role,
    ...(message.role === 'assistant'
      ? { feedbackTargetMessageId: message.message_id }
      : {}),
    content: message.content,
    events: message.rich_content.flatMap((payload) => {
      const event = parseStoredRichEvent(payload);
      return event === null ? [] : [event];
    }),
    mode: parseFolioMode(message.mode ?? response.conversation.current_mode),
    isStreaming: false,
  }));
}

export function useFolioStream(baseUrl: string = apiBaseUrl): UseFolioStreamResult {
  const [messages, setMessages] = useState<readonly FolioStreamMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isExecutingAction, setIsExecutingAction] = useState(false);
  const [currentMode, setCurrentMode] = useState<FolioMode | null>(null);
  const currentModeRef = useRef(currentMode);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const activeAssistantMessageIdRef = useRef<string | null>(null);
  const activeStreamIdRef = useRef<string | null>(null);
  const isStreamingRef = useRef(false);
  const isExecutingActionRef = useRef(false);
  const lastRequestRef = useRef<LastRequest | null>(null);
  const apiClient = useMemo(
    () => createContractAnalyzerAppApiClient({ baseUrl }),
    [baseUrl],
  );
  const isBusy = isStreaming || isExecutingAction;

  useEffect(() => {
    isStreamingRef.current = isStreaming;
  }, [isStreaming]);

  useEffect(() => {
    isExecutingActionRef.current = isExecutingAction;
  }, [isExecutingAction]);

  useEffect(() => {
    currentModeRef.current = currentMode;
  }, [currentMode]);

  const abortActiveStream = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    activeStreamIdRef.current = null;
    setIsStreaming(false);
    isStreamingRef.current = false;

    const assistantMessageId = activeAssistantMessageIdRef.current;
    activeAssistantMessageIdRef.current = null;
    if (assistantMessageId !== null) {
      setMessages((currentMessages) =>
        finalizeAssistantMessage(currentMessages, assistantMessageId),
      );
    }
  }, []);

  const clearMessages = useCallback(() => {
    abortActiveStream();
    setMessages([]);
    setIsExecutingAction(false);
    isExecutingActionRef.current = false;
    setCurrentMode(null);
    setError(null);
    lastRequestRef.current = null;
  }, [abortActiveStream]);

  useEffect(() => {
    return () => {
      abortActiveStream();
    };
  }, [abortActiveStream]);

  const startMessageStream = useCallback(
    async (
      trimmedText: string,
      conversationId: string | null,
      pageContext: PageContext | null,
      updateMessages?: (
        currentMessages: readonly FolioStreamMessage[],
      ) => readonly FolioStreamMessage[],
    ) => {
      await startFolioMessageStream(
        {
          folioChat: (input, opts) => apiClient.folioChat(input, opts),
          abortActiveStream,
          setMessages,
        setIsStreaming,
        setCurrentMode,
        setError,
        currentModeRef,
        abortControllerRef,
        activeAssistantMessageIdRef,
          activeStreamIdRef,
          isStreamingRef,
          lastRequestRef,
        },
        trimmedText,
        conversationId,
        pageContext,
        updateMessages,
      );
    },
    [abortActiveStream, apiClient],
  );

  const sendMessage = useCallback(
    async (text: string, conversationId?: string, pageContext?: PageContext) => {
      const trimmedText = text.trim();
      if (trimmedText.length === 0) {
        return;
      }

      await startMessageStream(trimmedText, conversationId ?? null, pageContext ?? null);
    },
    [startMessageStream],
  );

  const executeActionCard = useCallback(
    async (action: ActionCard, conversationId: string, pageContext?: PageContext) => {
      if (isStreamingRef.current || isExecutingActionRef.current) {
        throw new Error('Sean is already busy.');
      }

      setError(null);
      setIsExecutingAction(true);
      isExecutingActionRef.current = true;
      return await withCleanup(async () => {
        try {
          const response = normalizeActionExecutionResult(await apiClient.executeFolioAction({
            conversation_id: conversationId,
            action: {
              type: action.type,
              title: action.title,
              payload: action.payload ?? {},
            },
            ...(pageContext === undefined ? {} : { page_context: toApiPageContext(pageContext) }),
          }));
          const modeSnapshot = currentModeRef.current;
          setMessages((currentMessages) => [
            ...currentMessages,
            createMessage('user', response.user_message, modeSnapshot, false, {
              sourceRole: 'user',
            }),
            createMessage('assistant', response.assistant_message, modeSnapshot, false, {
              sourceRole: 'assistant',
            }),
          ]);
          return response;
        } catch (cause: unknown) {
          const errorMessage =
            cause instanceof Error && cause.message.trim().length > 0
              ? cause.message
              : 'Sean could not complete the request.';
          setError(errorMessage);
          if (cause instanceof Error) {
            throw cause;
          }
          throw new Error(errorMessage, { cause });
        }
      }, () => {
        setIsExecutingAction(false);
        isExecutingActionRef.current = false;
      });
    },
    [apiClient],
  );

  const retryLastMessage = useCallback(async () => {
    if (isStreamingRef.current) {
      return;
    }

    const lastRequest = lastRequestRef.current;
    if (lastRequest === null) {
      return;
    }

    await startMessageStream(
      lastRequest.text,
      lastRequest.conversationId,
      lastRequest.pageContext,
      (currentMessages) => removeMessagePair(currentMessages, lastRequest),
    );
  }, [startMessageStream]);

  const loadConversation = useCallback(
    async (conversationId: string): Promise<LoadedConversation> => {
      if (isStreamingRef.current || isExecutingActionRef.current) {
        throw new Error('Sean is already busy.');
      }

      abortActiveStream();
      setError(null);

      try {
        const response = await apiClient.getFolioConversation(conversationId);
        setMessages(mapStoredMessages(response));
        setCurrentMode(parseFolioMode(response.conversation.current_mode));
        lastRequestRef.current = null;
        return {
          conversationProjectId: response.conversation.project_id ?? null,
          lastPageContext: normalizeStoredPageContext(response.conversation.last_page_context),
        };
      } catch (cause: unknown) {
        const errorMessage =
          cause instanceof Error && cause.message.trim().length > 0
            ? cause.message
            : 'Sean could not load that conversation.';
        setError(errorMessage);
        if (cause instanceof Error) {
          throw cause;
        }
        throw new Error(errorMessage, { cause });
      }
    },
    [abortActiveStream, apiClient],
  );

  return {
    messages,
    isStreaming,
    isBusy,
    currentMode,
    error,
    sendMessage,
    executeActionCard,
    stopStream: abortActiveStream,
    retryLastMessage,
    loadConversation,
    clearMessages,
  };
}
